import { useMemo, useState } from 'react';
import { FileCheck2, Compass, PenSquare, ScanSearch, Search } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useLicitacoesFeed } from '@/hooks/useLicitacoes';
import { useViewModeStore } from '@/stores/view-mode';

type Ferramenta = { titulo: string; descricao: string; apoio: string; destino: string; icone: typeof Compass };

function Ferramentas() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode, previewEmpresaId } = useViewModeStore();
  const [seletorAberto, setSeletorAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const licitacaoId = Number(searchParams.get('licitacaoId')) || undefined;
  const empresaId = mode === 'empresa' ? previewEmpresaId : undefined;
  const hasContexto = Boolean(empresaId && licitacaoId);
  const { data: feed, isLoading } = useLicitacoesFeed({
    empresa_ids: empresaId ? [empresaId] : undefined,
    limit: 100,
    sort: 'prazo_abertura',
    order: 'asc',
  });
  const licitacoes = useMemo(() => (feed?.licitacoes || []).filter((licitacao) => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR');
    if (!termo) return true;
    return [licitacao.objeto, licitacao.orgao, licitacao.numero_edital, licitacao.municipio, licitacao.uf]
      .filter(Boolean)
      .some((valor) => valor!.toLocaleLowerCase('pt-BR').includes(termo));
  }), [busca, feed?.licitacoes]);
  const query = hasContexto ? `?empresaId=${empresaId}&licitacaoId=${licitacaoId}` : '';
  const ferramentas: Ferramenta[] = [
    { titulo: 'Entender o edital', descricao: 'Veja recomendação, aderência e riscos antes de decidir.', apoio: 'Oráculo do Edital', destino: `/agentes-ia/oraculo-edital${query}`, icone: Compass },
    { titulo: 'Preparar uma sugestão', descricao: 'Monte um rascunho para revisão humana a partir do edital.', apoio: 'Redator Ninja', destino: `/agentes-ia/redator-ninja${query}`, icone: PenSquare },
    { titulo: 'Verificar documentação', descricao: 'Confira requisitos e pendências para participar com segurança.', apoio: 'Auditor 360°', destino: `/agentes-ia/auditor-360${query}`, icone: ScanSearch },
  ];

  const selecionarLicitacao = (id: number) => {
    setSeletorAberto(false);
    setBusca('');
    navigate(`/agentes-ia/ferramentas?licitacaoId=${id}`);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-ds-text-primary"><FileCheck2 className="h-6 w-6 text-ds-brand" />Ferramentas</h1>
        <p className="mt-1 text-ds-text-secondary">Escolha o que você precisa fazer nesta licitação.</p>
      </header>

      {!hasContexto && (
        <Card className="border-ds-warning/30 bg-ds-warning/10">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
            <div>
              <h2 className="font-semibold">Escolha uma licitação primeiro</h2>
              <p className="mt-1 text-sm text-ds-text-secondary">Selecione o processo que você quer analisar. A empresa atual permanece vinculada.</p>
            </div>
            <Button variant="secondary" onClick={() => setSeletorAberto(true)} disabled={!empresaId}>Escolher licitação</Button>
          </CardContent>
        </Card>
      )}

      {hasContexto && (
        <div className="flex items-center justify-between rounded-xl border border-ds-brand/25 bg-ds-brand-subtle/30 px-4 py-3">
          <p className="text-sm text-ds-text-secondary">Licitação selecionada. Escolha a ação que precisa executar.</p>
          <Button variant="ghost" size="sm" className="text-ds-brand hover:bg-ds-brand-subtle/50" onClick={() => setSeletorAberto(true)}>Trocar licitação</Button>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {ferramentas.map(({ titulo, descricao, apoio, destino, icone: Icon }) => (
          <Card key={apoio} className="flex flex-col">
            <CardHeader><Icon className="h-6 w-6 text-ds-brand" /><CardTitle className="mt-3">{titulo}</CardTitle><CardDescription>{descricao}</CardDescription></CardHeader>
            <CardContent className="mt-auto">
              <p className="mb-3 text-xs text-ds-text-secondary">{apoio}</p>
              {hasContexto ? <Link to={destino}><Button className="w-full">Abrir</Button></Link> : <Button className="w-full" onClick={() => setSeletorAberto(true)} disabled={!empresaId}>Escolher licitação</Button>}
            </CardContent>
          </Card>
        ))}
      </div>

      <Modal isOpen={seletorAberto} onClose={() => setSeletorAberto(false)} title="Escolha uma licitação" contentClassName="max-w-3xl">
        <p className="mb-4 text-sm text-ds-text-secondary">Selecione uma oportunidade para manter o contexto da empresa e usar as ferramentas com segurança.</p>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-text-secondary" />
          <Input aria-label="Buscar licitação" value={busca} onChange={(event) => setBusca(event.target.value)} placeholder="Buscar por objeto, órgão, número ou cidade" className="pl-10" autoFocus />
        </div>
        <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1" aria-label="Licitações disponíveis">
          {isLoading && <p className="p-4 text-sm text-ds-text-secondary">Carregando licitações...</p>}
          {!isLoading && !empresaId && <p className="p-4 text-sm text-ds-warning">Selecione uma empresa na barra superior antes de escolher a licitação.</p>}
          {!isLoading && empresaId && licitacoes.length === 0 && <p className="p-4 text-sm text-ds-text-secondary">Nenhuma licitação encontrada para esta empresa.</p>}
          {licitacoes.map((licitacao) => (
            <button key={licitacao.id} type="button" onClick={() => selecionarLicitacao(licitacao.id)} className="w-full rounded-xl border border-ds-border bg-ds-surface p-4 text-left transition-colors hover:border-ds-brand/50 hover:bg-ds-brand-subtle/30 focus:outline-none focus:ring-2 focus:ring-ds-brand">
              <span className="block font-semibold text-ds-text-primary">{licitacao.objeto}</span>
              <span className="mt-1 block text-sm text-ds-text-secondary">{[licitacao.numero_edital, licitacao.orgao, licitacao.municipio && licitacao.uf ? `${licitacao.municipio} — ${licitacao.uf}` : licitacao.uf].filter(Boolean).join(' • ') || 'Detalhes não informados'}</span>
              {licitacao.prazo_abertura && <span className="mt-2 block text-xs text-ds-brand">Abertura: {new Date(licitacao.prazo_abertura).toLocaleDateString('pt-BR')}</span>}
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
}

export { Ferramentas };
